import { m } from '@hub/messages';
import { definePageRoute } from '@hub/router/definePageRoute';
import {
  createRoute,
  type DomainParents,
  defineDomainRoutes,
  lazyRouteComponent,
  notFound,
  redirect,
  retainSearchParams,
} from '@hub/router/domain-routes';
import { requireAuthentication } from '@hub/router/pages/_authenticated.guard';
import { publicHref } from '@hub/router/publicHref';
import { relativePathSchema } from '@hub/router/relativePath';
import { trackBeforeNav } from '@platform/tracking';
import { z } from 'zod';
import { captureBookingEntry } from '../../journey/booking';
import { HomePaths } from '../home/route.paths';
import { accountCompletionGuard, redirectCompletedAccountHome } from './accountCompletion.guard';
import { hasAppleLinkIntent } from './appleLinkIntent.storage';
import { redeemEmailConfirmation } from './confirmEmail.loader';
import { loadPasswordResetVerification } from './resetPassword.loader';
import { RETURN_TO_KEYS } from './returnTo';
import { LoginPaths } from './route.paths';

// No default: `to` is handed to the browser, and a default here could only be a canonical pattern.
export const authRedirectSearchSchema = z.object({
  to: relativePathSchema.optional(),
});

export default function createAuthRoutes(parents: DomainParents) {
  const authRedirectRoute = createRoute({
    getParentRoute: () => parents.public,
    path: LoginPaths.redirect.path,
    validateSearch: authRedirectSearchSchema,
    beforeLoad: ({ search }) => {
      trackBeforeNav('login_completed', { method: 'email' });
      throw redirect({ href: search.to ?? publicHref(HomePaths.home.path), replace: true });
    },
  });

  // Uses bare layout (no AppShell) because LoginPage provides its own fullscreen layout.
  // Lazy-loaded to break circular dependency: LoginPage → AuthContext → router → routeTree → here.
  const loginRoute = createRoute({
    getParentRoute: () => parents.bare,
    path: LoginPaths.login.path,
    validateSearch: LoginPaths.login.search,
    search: { middlewares: [retainSearchParams([...RETURN_TO_KEYS])] },
    // `href`, not `to`: the target is any in-app path, not a typed route.
    beforeLoad: ({ context, search }) => {
      if (context.auth.status !== 'authenticated') {
        return;
      }
      throw search.to ? redirect({ href: search.to, replace: true }) : redirect({ to: HomePaths.home.path });
    },
    component: lazyRouteComponent(() => import('./components/LoginPage'), 'LoginPage'),
  });

  const registerRoute = definePageRoute(parents, {
    path: LoginPaths.register,
    title: m.login_register_title,
    page: () => import('./Register.page'),
    flag: 'page-register',
    theme: 'dark-canvas',
    redirectAuthenticatedTo: HomePaths.home.path,
    hideHeaderUser: true,
    retainSearch: RETURN_TO_KEYS,
    // onEntry, not loader: an authenticated visitor is redirected out of beforeLoad, and a loader
    // would never run for them.
    onEntry: captureBookingEntry,
    preload: false,
  });

  const registerAccountRoute = definePageRoute(parents, {
    path: LoginPaths.registerAccount,
    title: m.login_register_account_title,
    page: () => import('./RegisterAccount.page'),
    flag: 'page-register',
    theme: 'dark-canvas',
    redirectAuthenticatedTo: HomePaths.home.path,
    hideHeaderUser: true,
    retainSearch: RETURN_TO_KEYS,
  });

  const registerProfileRoute = definePageRoute(parents, {
    path: LoginPaths.registerProfile,
    // The name the page renders, so tab and announcement agree.
    title: m.login_complete_registration_title,
    page: () => import('./RegisterProfile.page'),
    flag: 'page-register',
    theme: 'dark-canvas',
    redirectAuthenticatedTo: HomePaths.home.path,
    hideHeaderUser: true,
    retainSearch: RETURN_TO_KEYS,
  });

  const forgotPasswordRoute = definePageRoute(parents, {
    path: LoginPaths.forgotPassword,
    title: m.login_forgot_password_title,
    page: () => import('./ForgotPassword.page'),
  });

  const resetPasswordRoute = definePageRoute(parents, {
    path: LoginPaths.resetPassword,
    title: m.login_reset_password_title,
    page: () => import('./ResetPassword.page'),
    loaderDeps: ({ search }) => ({ token: search.token }),
    loader: ({ deps: { token } }) => loadPasswordResetVerification(token),
    // The loader renders the dead-link state in-page and only throws on transport failures, which
    // the domain's default error component handles with a retry. Off preload: verify is a POST.
    preload: false,
  });

  const logoutConfirmationRoute = definePageRoute(parents, {
    path: LoginPaths.logoutConfirmation,
    title: m.login_logout_confirmation_title,
    page: () => import('./LogoutConfirmation.page'),
  });

  // The layout guard handles the anonymous case: it bounces through login with the full href, so
  // the code rides along in `?to=`.
  const tvSigninRoute = definePageRoute(parents, {
    path: LoginPaths.tvSignin,
    title: m.login_tv_signin_title,
    description: m.login_tv_signin_description,
    page: () => import('./TvSignin.page'),
    flag: 'page-tv-signin',
    theme: 'dark-canvas',
    // The step brings its own headline, so Page's chrome would sit on top of a second heading.
    customLayout: true,
  });

  const accountLockedRoute = definePageRoute(parents, {
    // Neutral umbrella: the manifest title is static but the page has two variants
    // (generic lock / contract terminated), and both block sign-in.
    path: LoginPaths.accountLocked,
    title: m.login_account_locked_page_title,
    page: () => import('./AccountLocked.page'),
  });

  // Completion step (accountCompletionGuard target): journey route on `public`, not flag-gated. Not
  // `authenticated`, which runs the guard this route is the target of, so beforeLoad requires auth itself
  // and bounces already-complete accounts home (back/deep-link). Keeps the header account menu so the
  // trapped mrf user can log out. Dunning confinement deferred, same tradeoff as linkApple.
  const completeRegistrationRoute = createRoute({
    getParentRoute: () => parents.public,
    path: LoginPaths.completeRegistration.path,
    validateSearch: LoginPaths.completeRegistration.search,
    head: () => ({ meta: [{ title: m.login_complete_registration_title() }] }),
    staticData: { theme: 'dark-canvas' },
    beforeLoad: (opts) => {
      requireAuthentication(opts);
      redirectCompletedAccountHome(opts);
    },
    component: lazyRouteComponent(() => import('./CompleteRegistration.page')),
  });

  // Register-journey route on `public`, off the authenticated app-shell. beforeLoad requires auth (not the
  // completion guard: incomplete accounts must still reach linking, apple.md) then checks the confirm's
  // one-shot intent, so the route is reachable only from that flow; the page spends it on mount, not here.
  // Flag-gated off in prod. Dunning confinement still deferred (login must not import billing).
  const linkAppleRoute = createRoute({
    getParentRoute: () => parents.public,
    path: LoginPaths.linkApple.path,
    head: () => ({ meta: [{ title: m.login_apple_link_title() }] }),
    staticData: { hideHeaderUser: true, theme: 'dark-canvas' },
    beforeLoad: (opts) => {
      if (opts.context.featureFlags?.getFlag('page-register') !== true) {
        throw notFound();
      }
      requireAuthentication(opts);
      if (!hasAppleLinkIntent()) {
        throw redirect({ to: HomePaths.home.path, replace: true });
      }
    },
    component: lazyRouteComponent(() => import('./LinkApple.page')),
  });

  // Public — the token is proof on its own; a session is an enrichment, not the source of truth.
  const confirmEmailRoute = definePageRoute(parents, {
    path: LoginPaths.confirmEmail,
    title: m.login_confirm_email_title,
    page: () => import('./ConfirmEmail.page'),
    flag: 'page-account-confirm-email',
    loaderDeps: ({ search }) => ({ token: search.token }),
    loader: ({ deps: { token }, context }) => redeemEmailConfirmation(token, context.queryClient),
    preload: false,
  });

  // Same redemption, registration flow. No live traffic yet — see RegisterConfirmEmail.page.
  const registerConfirmEmailRoute = definePageRoute(parents, {
    path: LoginPaths.registerConfirmEmail,
    title: m.login_confirm_email_title,
    page: () => import('./RegisterConfirmEmail.page'),
    flag: 'page-register',
    theme: 'dark-canvas',
    loaderDeps: ({ search }) => ({ token: search.token }),
    loader: ({ deps: { token }, context }) => redeemEmailConfirmation(token, context.queryClient),
    preload: false,
  });

  return defineDomainRoutes({
    authenticated: [tvSigninRoute] as const,
    authenticatedGuards: [accountCompletionGuard] as const,
    publicGuards: [accountCompletionGuard] as const,
    public: [
      authRedirectRoute,
      forgotPasswordRoute,
      resetPasswordRoute,
      accountLockedRoute,
      registerRoute,
      registerAccountRoute,
      registerProfileRoute,
      linkAppleRoute,
      completeRegistrationRoute,
      confirmEmailRoute,
      registerConfirmEmailRoute,
    ] as const,
    bare: [loginRoute, logoutConfirmationRoute] as const,
  });
}
